import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import BottomNav from '../components/BottomNav';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { BookOpen, Scale, Sparkles, Briefcase, MailCheck } from 'lucide-react';
import { toast } from 'sonner';

export default function LandingPage() {
  const navigate = useNavigate();
  const { login, register, verifyAndLogin, resendVerification, user, loading: authLoading } = useAuth();
  const [activeTab, setActiveTab] = useState('login');
  const [loading, setLoading] = useState(false);

  // Login state
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [showVerification, setShowVerification] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [resending, setResending] = useState(false);

  // Register state
  const [registerData, setRegisterData] = useState({ email: '', password: '', full_name: '', referral_token: '' });

  useEffect(() => {
    if (user && !authLoading) {
      navigate('/library');
    }
  }, [user, authLoading]);

  const handleLogin = async (e) => {
    e.preventDefault();

    // If verification box is shown, do verify & login
    if (showVerification) {
      if (!verificationCode.trim()) {
        toast.error('Please enter the verification code');
        return;
      }
      setLoading(true);
      try {
        await verifyAndLogin(loginData.email, loginData.password, verificationCode.trim());
        toast.success('Trial activated! Welcome to La VidaLex.');
        navigate('/library');
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Verification failed');
      } finally {
        setLoading(false);
      }
      return;
    }

    // Normal login
    setLoading(true);
    try {
      const response = await login(loginData.email, loginData.password);

      if (response.requires_activation) {
        // Show the one-time verification box
        setShowVerification(true);
        toast.info('Please enter the verification code sent to your email.');
        setLoading(false);
        return;
      }

      if (response.requires_subscription) {
        toast.warning('Your trial has expired. Please subscribe to continue.');
        navigate('/subscribe');
      } else {
        toast.success('Welcome back!');
        navigate('/library');
      }
    } catch (error) {
      if (error.isDeviceLimitError) {
        toast.error(error.message, { duration: 6000 });
      } else {
        toast.error(error.response?.data?.detail || 'Login failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (!loginData.email) {
      toast.error('Please enter your email first');
      return;
    }
    setResending(true);
    try {
      await resendVerification(loginData.email);
      toast.success('A new verification code has been sent to your email.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend code');
    } finally {
      setResending(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await register(registerData.email, registerData.password, registerData.full_name, registerData.referral_token);
      if (response.requires_activation) {
        // Switch to login tab with email prefilled
        setLoginData({ email: registerData.email, password: registerData.password });
        setShowVerification(true);
        setActiveTab('login');
        toast.success('Account created! Check your email for the verification code.');
      } else if (response.token) {
        toast.success('Account created!');
        navigate('/library');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  // Reset verification box when email changes
  const handleLoginEmailChange = (email) => {
    setLoginData({ ...loginData, email });
    if (showVerification) {
      setShowVerification(false);
      setVerificationCode('');
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-50 dark:from-gray-900 dark:via-purple-900 dark:to-indigo-900">
        <div className="text-purple-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <div className="flex-1 flex flex-col lg:flex-row">
        {/* Left Section - Branding */}
        <div className="lg:w-1/2 relative text-white p-8 lg:p-16 flex flex-col justify-center items-center overflow-hidden">
          <div className="absolute inset-0" style={{ backgroundImage: 'url("/BG-LAVIDALEX.png")', backgroundSize: '100% 100%', backgroundPosition: 'center', backgroundRepeat: 'no-repeat' }} />

          <div className="max-w-xl w-full space-y-8 relative z-10">
            <div className="flex justify-center mb-6">
              <div className="p-1 bg-gradient-to-br from-sky-400 to-blue-500 rounded-xl shadow-2xl">
                <img src="/lavidalex-logo.png" alt="La VidaLex" className="h-40 w-40 sm:h-44 sm:w-44" />
              </div>
            </div>

            <div className="text-center space-y-3">
              <h1 className="text-4xl sm:text-5xl font-bold tracking-tight">
                La Vida<span className="text-sky-400">Lex</span>
              </h1>
              <p className="text-lg sm:text-xl text-sky-300 font-medium">
                Your Digital Pocketknife for Legal Work Life
              </p>
              <p className="text-base text-purple-200 italic mt-1">
                "Smart because of YOU. Fast because of AI."
              </p>
            </div>

            <div className="space-y-3 pt-6">
              <div className="bg-white/[0.08] backdrop-blur-xl border border-white/[0.25] rounded-xl p-4 hover:bg-white/[0.13] transition-all duration-300" style={{ boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.3), inset 0 -1px 0 rgba(255,255,255,0.05), 0 8px 32px rgba(0,0,0,0.15), 0 2px 8px rgba(0,0,0,0.1)' }}>
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-sky-400/20 rounded-lg shrink-0"><BookOpen className="w-5 h-5 text-sky-300" /></div>
                  <div>
                    <h3 className="font-bold text-base">Smart Law Library</h3>
                    <p className="text-base text-purple-200 mt-1">Access Philippine statutes, codes, and issuances with provision-level precision. Search by keyword, browse by codal category, or build your own library with the laws you actually need. Always current, authoritative, and ready.</p>
                  </div>
                </div>
              </div>

              <div className="bg-white/[0.08] backdrop-blur-xl border border-white/[0.25] rounded-xl p-4 hover:bg-white/[0.13] transition-all duration-300" style={{ boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.3), inset 0 -1px 0 rgba(255,255,255,0.05), 0 8px 32px rgba(0,0,0,0.15), 0 2px 8px rgba(0,0,0,0.1)' }}>
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-sky-400/20 rounded-lg shrink-0"><Scale className="w-5 h-5 text-sky-300" /></div>
                  <div>
                    <h3 className="font-bold text-base">Streamlined Supreme Court Jurisprudence Intelligence</h3>
                    <p className="text-base text-purple-200 mt-1">Browse the latest Supreme Court decisions alongside landmark cases that shaped Philippine law. Every decision features AI-generated digests, doctrine tags, and full-text search. Compare cases and find the ruling that matters in seconds, not hours.</p>
                  </div>
                </div>
              </div>

              <div className="bg-white/[0.08] backdrop-blur-xl border border-white/[0.25] rounded-xl p-4 hover:bg-white/[0.13] transition-all duration-300" style={{ boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.3), inset 0 -1px 0 rgba(255,255,255,0.05), 0 8px 32px rgba(0,0,0,0.15), 0 2px 8px rgba(0,0,0,0.1)' }}>
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-sky-400/20 rounded-lg shrink-0"><Sparkles className="w-5 h-5 text-sky-300" /></div>
                  <div>
                    <h3 className="font-bold text-base">AI Legal Consultant</h3>
                    <p className="text-base text-purple-200 mt-1">Ask Lois any legal question and get a structured answer grounded in statutory provisions and jurisprudence, with inline citations you can verify. Turn research into citation-backed, litigation-ready output designed for serious legal work.</p>
                  </div>
                </div>
              </div>

              <div className="bg-white/[0.08] backdrop-blur-xl border border-white/[0.25] rounded-xl p-4 hover:bg-white/[0.13] transition-all duration-300" style={{ boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.3), inset 0 -1px 0 rgba(255,255,255,0.05), 0 8px 32px rgba(0,0,0,0.15), 0 2px 8px rgba(0,0,0,0.1)' }}>
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-sky-400/20 rounded-lg shrink-0"><Briefcase className="w-5 h-5 text-sky-300" /></div>
                  <div>
                    <h3 className="font-bold text-base">Workspace and QUAMTO Masterclass</h3>
                    <p className="text-base text-purple-200 mt-1">Bookmark provisions, annotate cases, and organize your research in one place. Strengthen doctrine recall and issue-spotting with AI-powered Q&A sets from QUAMTO (Questions Asked More Than Once) in Philippine bar exams. Your notes, cases, and review tools in a single workflow.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Section - Auth Forms */}
        <div className="lg:w-1/2 bg-white flex items-center justify-center p-8 overflow-y-auto">
          <div className="w-full max-w-md space-y-6">
            {/* QR Code */}
            <div className="flex justify-center">
              <img
                src="/qrcode-lavidalex.png"
                alt="La VidaLex QR Code"
                className="w-28 h-28 rounded-md"
                style={{ boxShadow: '0 8px 24px rgba(0,0,0,0.18), 0 2px 8px rgba(0,0,0,0.12)' }}
                data-testid="login-qr-code"
              />
            </div>

            {/* Developer Bio */}
            <p className="text-center text-base text-gray-700 font-semibold leading-relaxed px-2">
              Developed by a Philippine lawyer with over 25 years of experience in law, policy and governance, La VidaLex is built on real-world understanding of how laws and legal processes work. With extensive practice in drafting legal documents, she applies a proven research quality system that ensures clarity, coherence and impact. A self-taught app developer, she combines legal insights and tech skills to transform complex workflows into digital tools that help professionals achieve accuracy, speed and compliance.
            </p>

            <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); if (v === 'register') { setShowVerification(false); setVerificationCode(''); } }}>
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="login" data-testid="login-tab">Login</TabsTrigger>
                <TabsTrigger value="register" data-testid="register-tab">Register</TabsTrigger>
              </TabsList>

              {/* Login Form */}
              <TabsContent value="login">
                <Card>
                  <CardHeader>
                    <CardTitle>Welcome Back</CardTitle>
                    <CardDescription>Enter your credentials to access your library</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleLogin} className="space-y-4" data-testid="login-form">
                      <div>
                        <Label htmlFor="login-email">Email</Label>
                        <Input
                          id="login-email"
                          type="email"
                          placeholder="your@email.com"
                          value={loginData.email}
                          onChange={(e) => handleLoginEmailChange(e.target.value)}
                          required
                          data-testid="login-email-input"
                        />
                      </div>
                      <div>
                        <Label htmlFor="login-password">Password</Label>
                        <PasswordInput
                          id="login-password"
                          placeholder="Enter password"
                          value={loginData.password}
                          onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                          required
                          data-testid="login-password-input"
                        />
                      </div>

                      {/* One-Time Verification Code Box */}
                      {showVerification && (
                        <div className="border-2 border-yellow-300 bg-yellow-50 rounded-lg p-4 space-y-2" data-testid="verification-box">
                          <div className="flex items-center gap-2">
                            <MailCheck className="w-5 h-5 text-yellow-700" />
                            <span className="font-semibold text-gray-800 text-sm">One-Time Verification Code</span>
                          </div>
                          <Input
                            type="text"
                            placeholder="Enter 6-digit code from email"
                            value={verificationCode}
                            onChange={(e) => {
                              const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                              setVerificationCode(val);
                            }}
                            className="text-center text-lg tracking-widest font-mono"
                            maxLength={6}
                            data-testid="verification-code-input"
                          />
                          <p className="text-xs text-orange-700">
                            Check your email ({loginData.email}) for the verification code
                          </p>
                          <button
                            type="button"
                            onClick={handleResendCode}
                            disabled={resending}
                            className="text-xs text-orange-600 hover:text-orange-800 underline"
                            data-testid="resend-code-btn"
                          >
                            {resending ? 'Sending...' : "Didn't receive? Resend code"}
                          </button>
                        </div>
                      )}

                      <div className="text-right">
                        <a href="/forgot-password" className="text-base text-purple-600 hover:text-purple-800">
                          Forgot Password?
                        </a>
                      </div>
                      <Button
                        type="submit"
                        className="w-full bg-purple-600 hover:bg-purple-700 text-base"
                        disabled={loading}
                        data-testid="login-submit-btn"
                      >
                        {loading
                          ? (showVerification ? 'Verifying...' : 'Logging in...')
                          : (showVerification ? 'Verify & Login' : 'Login')
                        }
                      </Button>
                    </form>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Register Form */}
              <TabsContent value="register">
                <Card>
                  <CardHeader>
                    <CardTitle>Create Account</CardTitle>
                    <CardDescription>Register to start your free trial</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleRegister} className="space-y-4" data-testid="register-form">
                      <div>
                        <Label htmlFor="register-name">Full Name</Label>
                        <Input
                          id="register-name"
                          type="text"
                          placeholder="Juan Dela Cruz"
                          value={registerData.full_name}
                          onChange={(e) => setRegisterData({ ...registerData, full_name: e.target.value })}
                          required
                          data-testid="register-name-input"
                        />
                      </div>
                      <div>
                        <Label htmlFor="register-email">Email</Label>
                        <Input
                          id="register-email"
                          type="email"
                          placeholder="your@email.com"
                          value={registerData.email}
                          onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                          required
                          data-testid="register-email-input"
                        />
                      </div>
                      <div>
                        <Label htmlFor="register-password">Password</Label>
                        <PasswordInput
                          id="register-password"
                          placeholder="Create a password"
                          value={registerData.password}
                          onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                          required
                          data-testid="register-password-input"
                        />
                      </div>
                      <div>
                        <Label htmlFor="register-referral">Referral Code (Optional)</Label>
                        <Input
                          id="register-referral"
                          type="text"
                          placeholder="VIDALEX-ABC123"
                          value={registerData.referral_token}
                          onChange={(e) => setRegisterData({ ...registerData, referral_token: e.target.value })}
                          data-testid="register-referral-input"
                        />
                      </div>
                      <Button
                        type="submit"
                        className="w-full bg-purple-600 hover:bg-purple-700 text-base"
                        disabled={loading}
                        data-testid="register-submit-btn"
                      >
                        {loading ? 'Creating Account...' : 'Create Account'}
                      </Button>
                    </form>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>

            {/* Footer Links */}
            <div className="text-center text-base text-gray-600 space-y-2">
              <div>
                <a href="/terms" className="hover:text-purple-600">Terms of Service</a>
                {' \u2022 '}
                <a href="/privacy" className="hover:text-purple-600">Privacy Policy</a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
